import { useCallback, useEffect, useState } from "react";
import { IoCubeOutline } from "react-icons/io5";
import { LuCopyPlus } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import ModalCadastroCategoriaEstoque from "../../components/modais/ModalCadastroCategoriaEstoque";
import ModalCadastroUnidadeMedida from "../../components/modais/ModalCadastroUnidadeMedida";
import { api } from "../../services/api";
import {
  repositorioCategoriaEstoque,
  repositorioEstoqueItem,
  repositorioUnidadeMedida,
} from "../../sync/repositories/almoxarifado";

const AJUDA = {
  titulo: "Cadastro de Itens de Estoque",
  descricao:
    "Cadastre aqui cada produto ou material controlado pelo almoxarifado (insumos, produtos para venda, ferramentas/ativos). Esse cadastro é usado depois nos Acertos de Estoque e aparece nas Movimentações — é ele que mantém o saldo e o custo de cada item.",
  secoes: [
    {
      titulo: "Informações Básicas",
      campos: [
        {
          nome: "Nome do Item",
          obrigatorio: true,
          explicacao:
            "Nome completo do produto ou material (ex.: 'Herbicida Glifosato', 'Trator'). É o que aparece nas listas de seleção em Acertos e Movimentações.",
        },
        {
          nome: "Tipo do Item",
          obrigatorio: true,
          explicacao:
            "Classifica o item como Insumo Agrícola/Pecuário, Produto Final (Venda) ou Ativo Imobilizado/Ferramenta — isso muda como o item é tratado nos relatórios.",
        },
        {
          nome: "Código Interno",
          obrigatorio: false,
          explicacao:
            "Identificador único usado internamente na fazenda. Se deixado em branco, pode ser preenchido depois sem prejuízo ao cadastro.",
        },
      ],
    },
    {
      titulo: "Classificação",
      campos: [
        {
          nome: "Categoria",
          obrigatorio: false,
          explicacao:
            "A Categoria (cadastrada em Categorias de Estoque) usada para organizar este item nas listagens e filtros. Use o botão de atalho ao lado do campo para criar uma nova categoria sem sair da tela.",
        },
        {
          nome: "Unidade de Medida",
          obrigatorio: true,
          explicacao:
            "A unidade (cadastrada em Unidades de Medida) em que a quantidade deste item é contada — kg, litro, unidade, etc. Define como o saldo e as movimentações desse item são exibidos.",
        },
        {
          nome: "NCM",
          obrigatorio: false,
          explicacao:
            "Código de 8 dígitos da Nomenclatura Comum do Mercosul, usado para identificação fiscal do produto (relevante em notas fiscais).",
        },
      ],
    },
    {
      titulo: "Valores e Estoque",
      campos: [
        {
          nome: "Custo Unitário (R$)",
          obrigatorio: false,
          explicacao:
            "Valor pago por unidade na aquisição do item — usado para calcular o valor total do estoque nos relatórios.",
        },
        {
          nome: "Preço de Venda (R$)",
          obrigatorio: false,
          explicacao:
            "Preço sugerido de venda por unidade, mais relevante para itens do tipo 'Produto Final (Venda)'.",
        },
        {
          nome: "Saldo em Estoque",
          obrigatorio: false,
          explicacao:
            "Quantidade inicial disponível deste item no momento do cadastro. Depois de criado, o saldo passa a ser atualizado pelas Movimentações e Acertos de Estoque, não editando este campo diretamente.",
        },
      ],
    },
    {
      titulo: "Identificadores",
      campos: [
        {
          nome: "Código de Barras",
          obrigatorio: false,
          explicacao: "Código EAN-13 ou similar impresso no produto, se houver.",
        },
        {
          nome: "Código do Fornecedor",
          obrigatorio: false,
          explicacao:
            "Identificação que o fornecedor usa para este mesmo produto — útil para conferência ao receber notas fiscais.",
        },
      ],
    },
  ],
  dicas: [
    "Excluir um item que já tem movimentações vinculadas não é permitido — o sistema oferece a opção de inativá-lo, o que o remove das listas de seleção sem apagar o histórico.",
    "O filtro de Status na listagem mostra 'Somente ativos' por padrão — itens inativados só aparecem selecionando 'Somente inativos' ou 'Todos'.",
  ],
};

export default function GestaoItensEstoque() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [itens, setItens] = useState([]);
  const [itensFiltrados, setItensFiltrados] = useState([]);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);

  // Dados para os selects
  const [categorias, setCategorias] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [mostrarModalCategoria, setMostrarModalCategoria] = useState(false);
  const [mostrarModalUnidade, setMostrarModalUnidade] = useState(false);

  // Filtros
  const [filtros, setFiltros] = useState({
    busca: "",
    tipo: "",
    categoria: "",
    status: "ativos",
  });

  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina] = useState(10);

  const [formData, setFormData] = useState({
    nome: "",
    tipo: "insumo",
    categoria_detalhada: "",
    unidade_medida: "",
    ncm: "",
    codigo_interno: "",
    custo_unitario: "",
    preco_venda: "",
    saldo_atual: "",
    codigo_barras: "",
    codigo_fornecedor: "",
  });

  // Tipos de item
  const tiposItem = [
    {
      value: "insumo",
      label: "Insumo Agrícola/Pecuário",
      descricao: "Produtos utilizados na produção",
    },
    {
      value: "produto_final",
      label: "Produto Final (Venda)",
      descricao: "Produtos prontos para venda",
    },
    {
      value: "ativo",
      label: "Ativo Imobilizado/Ferramenta",
      descricao: "Equipamentos e ferramentas",
    },
  ];

  // Carregar categorias
  const carregarCategorias = useCallback(async () => {
    try {
      const response = await repositorioCategoriaEstoque.listar();
      const dados = response.data.results || response.data;
      setCategorias(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
      setCategorias([]);
    }
  }, []);

  // Carregar unidades de medida
  const carregarUnidades = useCallback(async () => {
    try {
      const response = await repositorioUnidadeMedida.listar();
      const dados = response.data.results || response.data;
      setUnidades(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar unidades:", error);
      setUnidades([]);
    }
  }, []);

  // Carregar itens de estoque
  const carregarItens = useCallback(async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioEstoqueItem.listar();

      const dados = response.data.results || response.data;
      const lista = Array.isArray(dados) ? dados : [];

      setItens(lista);

      const ativos = lista.filter((item) => item.ativo !== false);
      setItensFiltrados(ativos);
    } catch (error) {
      console.error("Erro ao carregar itens:", error);
      alert("Erro ao carregar lista de itens");
    } finally {
      setCarregandoLista(false);
    }
  }, []);

  useEffect(() => {
    carregarItens();
    carregarCategorias();
    carregarUnidades();
  }, [carregarItens, carregarCategorias, carregarUnidades]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validarFormulario = () => {
    if (!formData.nome.trim()) {
      alert("Informe o nome do item.");
      return false;
    }
    if (!formData.tipo) {
      alert("Selecione o tipo do item.");
      return false;
    }
    if (!formData.unidade_medida) {
      alert("Selecione a unidade de medida.");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) return;

    setLoading(true);

    const payload = {
      nome: formData.nome,
      tipo: formData.tipo,
      categoria_detalhada_id: formData.categoria_detalhada || null,
      unidade_medida_id: formData.unidade_medida,
      saldo_atual: formData.saldo_atual ? Number(formData.saldo_atual) : 0,
      custo_unitario: formData.custo_unitario
        ? Number(formData.custo_unitario)
        : 0,
      preco_venda: formData.preco_venda ? Number(formData.preco_venda) : 0,
      ncm: formData.ncm || null,
      codigo_interno: formData.codigo_interno || null,
      codigo_barras: formData.codigo_barras || null,
      codigo_fornecedor: formData.codigo_fornecedor || null,
    };

    // Remove campos vazios
    Object.keys(payload).forEach((key) => {
      if (payload[key] === null || payload[key] === "") delete payload[key];
    });

    try {
      if (editando) {
        await repositorioEstoqueItem.atualizar(editandoId, payload);
        alert("✅ Item atualizado com sucesso!");
        handleCancel();
        carregarItens();
      } else {
        await repositorioEstoqueItem.criar(payload);
        alert("✅ Item cadastrado com sucesso!");
        handleCancel();
        carregarItens();
      }
    } catch (error) {
      console.error("Erro:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert("Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditar = (item) => {
    setFormData({
      nome: item.nome || "",
      tipo: item.tipo || "insumo",
      categoria_detalhada:
        item.categoria_detalhada?.id || item.categoria_detalhada || "",
      unidade_medida: item.unidade_medida?.id || item.unidade_medida || "",
      ncm: item.ncm || "",
      codigo_interno: item.codigo_interno || "",
      custo_unitario: item.custo_unitario || "",
      preco_venda: item.preco_venda || "",
      saldo_atual: item.saldo_atual || "",
      codigo_barras: item.codigo_barras || "",
      codigo_fornecedor: item.codigo_fornecedor || "",
    });
    setEditando(true);
    setEditandoId(item.id);
    setMostrarFormulario(true);

    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  const handleNovoItem = () => {
    handleCancel();
    setMostrarFormulario(true);
  };

  const handleCancel = () => {
    setFormData({
      nome: "",
      tipo: "insumo",
      categoria_detalhada: "",
      unidade_medida: "",
      ncm: "",
      codigo_interno: "",
      custo_unitario: "",
      preco_venda: "",
      saldo_atual: "",
      codigo_barras: "",
      codigo_fornecedor: "",
    });
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("⚠️ Tem certeza que deseja excluir este item?")) {
      return;
    }

    try {
      await repositorioEstoqueItem.excluir(id);
      alert("✅ Item excluído com sucesso!");

      if (editandoId === id) {
        handleCancel();
      }

      carregarItens();
    } catch (error) {
      console.error("Erro ao excluir:", error);

      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const code = error.response?.data?.code;

      // Se backend avisar que o item possui movimentação vinculada
      if (
        status === 409 &&
        (code === "item_com_movimentacao" ||
          detail?.toLowerCase().includes("movimenta"))
      ) {
        const confirmarInativacao = window.confirm(
          "Este item possui movimentações vinculadas e não pode ser excluído.\n\nDeseja inativá-lo?",
        );

        if (!confirmarInativacao) return;

        try {
          await api.patch(`/almoxarifado/itens-estoque/${id}/`, {
            ativo: false,
          });

          alert("✅ Item inativado com sucesso!");

          if (editandoId === id) {
            handleCancel();
          }

          carregarItens();
        } catch (erroInativar) {
          console.error("Erro ao inativar item:", erroInativar);
          alert("❌ Não foi possível inativar o item.");
        }

        return;
      }

      alert("❌ Erro ao excluir item.");
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    let filtrados = Array.isArray(itens) ? [...itens] : [];

    if (filtros.busca) {
      const buscaLower = filtros.busca.toLowerCase();
      filtrados = filtrados.filter(
        (i) =>
          i.nome?.toLowerCase().includes(buscaLower) ||
          i.codigo_interno?.toLowerCase().includes(buscaLower) ||
          i.codigo_barras?.toLowerCase().includes(buscaLower),
      );
    }

    if (filtros.tipo) {
      filtrados = filtrados.filter((i) => i.tipo === filtros.tipo);
    }

    if (filtros.categoria) {
      filtrados = filtrados.filter(
        (i) =>
          i.categoria_detalhada?.id === parseInt(filtros.categoria) ||
          i.categoria_detalhada === parseInt(filtros.categoria),
      );
    }

    if (filtros.status === "ativos") {
      filtrados = filtrados.filter((i) => i.ativo !== false);
    } else if (filtros.status === "inativos") {
      filtrados = filtrados.filter((i) => i.ativo === false);
    }

    setItensFiltrados(filtrados);
    setPaginaAtual(1);
  };

  const limparFiltros = () => {
    const novosFiltros = {
      busca: "",
      tipo: "",
      categoria: "",
      status: "ativos",
    };

    setFiltros(novosFiltros);

    const ativos = Array.isArray(itens)
      ? itens.filter((item) => item.ativo !== false)
      : [];

    setItensFiltrados(ativos);
    setPaginaAtual(1);
  };

  const getTipoLabel = (tipo) => {
    const tipoEncontrado = tiposItem.find((t) => t.value === tipo);
    return tipoEncontrado ? tipoEncontrado.label : tipo;
  };

  const getTipoColor = (tipo) => {
    const cores = {
      insumo: "bg-green-100 text-green-800",
      produto_final: "bg-blue-100 text-blue-800",
      ativo: "bg-yellow-100 text-yellow-800",
    };
    return cores[tipo] || "bg-gray-100 text-gray-800";
  };

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return "—";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(valor);
  };

  const formatarQuantidade = (quantidade) => {
    if (!quantidade && quantidade !== 0) return "—";
    return Number(quantidade).toLocaleString("pt-BR", {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    });
  };

  const formatarNCM = (ncm) => {
    if (!ncm) return "—";
    if (ncm.length === 8) {
      return `${ncm.substring(0, 4)}.${ncm.substring(4, 6)}.${ncm.substring(6, 8)}`;
    }
    return ncm;
  };

  const indiceUltimoItem = paginaAtual * itensPorPagina;
  const indicePrimeiroItem = indiceUltimoItem - itensPorPagina;
  const itensPaginados = itensFiltrados.slice(
    indicePrimeiroItem,
    indiceUltimoItem,
  );
  const totalPaginas = Math.ceil(itensFiltrados.length / itensPorPagina);

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              Gestão de Itens de Estoque <IoCubeOutline size={38} />
            </h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovoItem}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Novo Item
            </button>
          )}
        </div>

        {/* Formulário de Cadastro/Edição */}
        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {editando ? "Editar Item" : "Novo Item de Estoque"}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-500 hover:text-gray-700"
                title="Fechar formulário"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                {/* Seção 1: Informações Básicas */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-700 mb-4">
                    Informações Básicas
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Nome do Item *
                      </label>
                      <input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Ex: Herbicida Glifosato, Semente Soja, Trator..."
                        required
                      />
                      <span className="text-xs text-gray-500 mt-1">
                        Nome completo do produto/material
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Tipo do Item *
                      </label>
                      <select
                        name="tipo"
                        value={formData.tipo}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        required
                      >
                        <option value="">Selecione...</option>
                        {tiposItem.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                      <span className="text-xs text-gray-500 mt-1">
                        {
                          tiposItem.find((t) => t.value === formData.tipo)
                            ?.descricao
                        }
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Código Interno
                      </label>
                      <input
                        type="text"
                        name="codigo_interno"
                        value={formData.codigo_interno}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Deixe em branco para gerar automaticamente"
                      />
                      <span className="text-xs text-gray-500 mt-1">
                        Identificador único interno (opcional)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Seção 2: Classificação */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-700 mb-4">
                    Classificação
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Categoria
                      </label>
                      <div className="flex items-center gap-2">
                        <select
                          name="categoria_detalhada"
                          value={formData.categoria_detalhada}
                          onChange={handleChange}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        >
                          <option value="">Selecione uma categoria...</option>
                          {Array.isArray(categorias) &&
                            categorias.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.nome}
                              </option>
                            ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setMostrarModalCategoria(true)}
                          title="Cadastrar nova categoria"
                          className="p-2 hover:bg-gray-200 rounded"
                        >
                          <LuCopyPlus
                            size={26}
                            className="text-green-600 hover:text-green-800"
                          />
                        </button>
                      </div>
                      <span className="text-xs text-gray-500 mt-1">
                        Categoria para organização do estoque
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Unidade de Medida *
                      </label>
                      <div className="flex items-center gap-2">
                        <select
                          name="unidade_medida"
                          value={formData.unidade_medida}
                          onChange={handleChange}
                          className="flex-1 border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                          required
                        >
                          <option value="">Selecione...</option>
                          {Array.isArray(unidades) &&
                            unidades.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.sigla} - {u.descricao}
                              </option>
                            ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setMostrarModalUnidade(true)}
                          title="Cadastrar nova unidade"
                          className="p-2 hover:bg-gray-200 rounded"
                        >
                          <LuCopyPlus
                            size={26}
                            className="text-green-600 hover:text-green-800"
                          />
                        </button>
                      </div>
                      <span className="text-xs text-gray-500 mt-1">
                        Unidade de medida do item (kg, L, un, etc.)
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        NCM
                      </label>
                      <input
                        type="text"
                        name="ncm"
                        maxLength={8}
                        value={formData.ncm}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="00000000"
                      />
                      <span className="text-xs text-gray-500 mt-1">
                        Nomenclatura Comum do Mercosul (8 dígitos)
                      </span>
                    </div>
                  </div>
                </div>

                {/* Seção 3: Valores e Estoque */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-700 mb-4">
                    Valores e Estoque
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Custo Unitário (R$)
                      </label>
                      <div className="flex items-center border border-green-500 rounded">
                        <span className="px-3 text-gray-700 bg-gray-100 rounded-l">
                          R$
                        </span>
                        <input
                          type="number"
                          step="0.0001"
                          name="custo_unitario"
                          value={formData.custo_unitario}
                          onChange={handleChange}
                          className="flex-1 p-2 rounded-r focus:ring-2 focus:ring-green-300"
                          placeholder="0,0000"
                        />
                      </div>
                      <span className="text-xs text-gray-500 mt-1">
                        Custo de aquisição do item
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Preço de Venda (R$)
                      </label>
                      <div className="flex items-center border border-green-500 rounded">
                        <span className="px-3 text-gray-700 bg-gray-100 rounded-l">
                          R$
                        </span>
                        <input
                          type="number"
                          step="0.0001"
                          name="preco_venda"
                          value={formData.preco_venda}
                          onChange={handleChange}
                          className="flex-1 p-2 rounded-r focus:ring-2 focus:ring-green-300"
                          placeholder="0,0000"
                        />
                      </div>
                      <span className="text-xs text-gray-500 mt-1">
                        Preço de venda sugerido
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Saldo em Estoque
                      </label>
                      <input
                        type="number"
                        step="0.001"
                        name="saldo_atual"
                        value={formData.saldo_atual}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="0,000"
                      />
                      <span className="text-xs text-gray-500 mt-1">
                        Quantidade atual em estoque
                      </span>
                    </div>
                  </div>
                </div>

                {/* Seção 4: Identificadores */}
                <div className="border-b pb-4">
                  <h4 className="text-lg font-semibold text-gray-700 mb-4">
                    Identificadores
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Código de Barras
                      </label>
                      <input
                        type="text"
                        name="codigo_barras"
                        value={formData.codigo_barras}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Código de barras do produto"
                      />
                      <span className="text-xs text-gray-500 mt-1">
                        Código EAN-13 ou similar
                      </span>
                    </div>

                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Código do Fornecedor
                      </label>
                      <input
                        type="text"
                        name="codigo_fornecedor"
                        value={formData.codigo_fornecedor}
                        onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="Código do produto no fornecedor"
                      />
                      <span className="text-xs text-gray-500 mt-1">
                        Identificação usada pelo fornecedor
                      </span>
                    </div>
                  </div>
                </div>

                {/* Botões do formulário */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                  >
                    {loading
                      ? "Salvando..."
                      : editando
                        ? "Atualizar"
                        : "Cadastrar"}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Filtros e Tabela - visíveis apenas quando formulário está fechado */}
        {!mostrarFormulario && (
          <>
            {/* Seção de Filtros */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
                Filtros de Busca
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    Buscar por Nome ou Código
                  </label>
                  <input
                    type="text"
                    placeholder="Nome, código interno ou código de barras..."
                    value={filtros.busca}
                    onChange={(e) =>
                      setFiltros({ ...filtros, busca: e.target.value })
                    }
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    Tipo do Item
                  </label>
                  <select
                    value={filtros.tipo}
                    onChange={(e) =>
                      setFiltros({ ...filtros, tipo: e.target.value })
                    }
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="">Todos os tipos</option>
                    {tiposItem.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    Categoria
                  </label>
                  <select
                    value={filtros.categoria}
                    onChange={(e) =>
                      setFiltros({ ...filtros, categoria: e.target.value })
                    }
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="">Todas as categorias</option>
                    {categorias.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.nome}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filtros.status}
                    onChange={(e) =>
                      setFiltros({ ...filtros, status: e.target.value })
                    }
                    className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                  >
                    <option value="ativos">Somente ativos</option>
                    <option value="inativos">Somente inativos</option>
                    <option value="todos">Todos</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-4">
                <button
                  onClick={aplicarFiltros}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={limparFiltros}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors"
                >
                  Limpar Filtros
                </button>
              </div>
            </div>

            {/* Tabela de Itens */}
            <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center flex-wrap gap-2">
                <p className="text-gray-600">
                  {itensFiltrados.length} item(ns) encontrado(s)
                </p>
                <div className="text-sm text-gray-500">
                  Página {paginaAtual} de {totalPaginas || 1}
                </div>
              </div>

              {carregandoLista ? (
                <div className="p-8 text-center text-gray-500">
                  ⏳ Carregando...
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">
                            Código
                          </th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">
                            Nome
                          </th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">
                            Tipo
                          </th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">
                            Unidade
                          </th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">
                            Saldo
                          </th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">
                            Custo
                          </th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">
                            Venda
                          </th>
                          <th className="p-3 text-left text-sm font-semibold text-gray-700">
                            Status
                          </th>
                          <th className="p-3 text-center text-sm font-semibold text-gray-700">
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {itensPaginados.map((item) => (
                          <tr
                            key={item.id}
                            className="border-t border-gray-200 hover:bg-gray-50 transition-colors"
                          >
                            <td className="p-3 text-sm font-mono">
                              {item.codigo_interno || item.id}
                            </td>
                            <td className="p-3 text-sm font-medium">
                              {item.nome}
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${getTipoColor(item.tipo)}`}
                              >
                                {getTipoLabel(item.tipo)}
                              </span>
                            </td>
                            <td className="p-3 text-sm">
                              {item.unidade_medida?.sigla || "—"}
                            </td>
                            <td className="p-3 text-sm font-medium">
                              {formatarQuantidade(item.saldo_atual)}
                            </td>
                            <td className="p-3 text-sm">
                              {formatarMoeda(item.custo_unitario)}
                            </td>
                            <td className="p-3 text-sm">
                              {formatarMoeda(item.preco_venda)}
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  item.ativo === false
                                    ? "bg-red-100 text-red-700"
                                    : "bg-green-100 text-green-700"
                                }`}
                              >
                                {item.ativo === false ? "Inativo" : "Ativo"}
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="flex justify-center gap-2">
                                <button
                                  onClick={() => handleEditar(item)}
                                  className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                                  title="Editar"
                                >
                                  Editar
                                </button>
                                <button
                                  onClick={() => handleExcluir(item.id)}
                                  className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                  title="Excluir"
                                >
                                  Excluir
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}

                        {itensPaginados.length === 0 && (
                          <tr>
                            <td
                              colSpan="9"
                              className="p-8 text-center text-gray-500"
                            >
                              {filtros.busca ||
                              filtros.tipo ||
                              filtros.categoria
                                ? "Nenhum item encontrado com os filtros selecionados."
                                : "Nenhum item de estoque cadastrado."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Paginação */}
                  {totalPaginas > 1 && (
                    <div className="p-4 border-t border-gray-200 bg-gray-50 flex flex-col md:flex-row justify-between items-center gap-4">
                      <div className="text-sm text-gray-600">
                        Exibindo {indicePrimeiroItem + 1} até{" "}
                        {Math.min(indiceUltimoItem, itensFiltrados.length)} de{" "}
                        {itensFiltrados.length} item(ns)
                      </div>

                      <div className="flex items-center gap-2 flex-wrap justify-center">
                        <button
                          onClick={() =>
                            setPaginaAtual((prev) => Math.max(prev - 1, 1))
                          }
                          disabled={paginaAtual === 1}
                          className={`px-3 py-1 rounded transition-colors ${
                            paginaAtual === 1
                              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                              : "bg-blue-600 text-white hover:bg-blue-700"
                          }`}
                        >
                          ◀ Anterior
                        </button>

                        {Array.from(
                          { length: totalPaginas },
                          (_, i) => i + 1,
                        ).map((num) => (
                          <button
                            key={num}
                            onClick={() => setPaginaAtual(num)}
                            className={`w-9 h-9 rounded transition-colors ${
                              paginaAtual === num
                                ? "bg-green-600 text-white font-semibold"
                                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                          >
                            {num}
                          </button>
                        ))}

                        <button
                          onClick={() =>
                            setPaginaAtual((prev) =>
                              Math.min(prev + 1, totalPaginas),
                            )
                          }
                          disabled={paginaAtual === totalPaginas}
                          className={`px-3 py-1 rounded transition-colors ${
                            paginaAtual === totalPaginas
                              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                              : "bg-blue-600 text-white hover:bg-blue-700"
                          }`}
                        >
                          Próxima ▶
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      <ModalCadastroCategoriaEstoque
        isOpen={mostrarModalCategoria}
        onClose={() => setMostrarModalCategoria(false)}
        onSuccess={async (nova) => {
          await carregarCategorias();
          setFormData((prev) => ({ ...prev, categoria_detalhada: nova.id }));
        }}
      />
      <ModalCadastroUnidadeMedida
        isOpen={mostrarModalUnidade}
        onClose={() => setMostrarModalUnidade(false)}
        onSuccess={async (nova) => {
          await carregarUnidades();
          setFormData((prev) => ({ ...prev, unidade_medida: nova.id }));
        }}
      />
    </Layout>
  );
}
